import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  Commande,
  CommandeFilterInput,
  CommandesFinancieresPaginatedCollectionSegment,
  ConseilConfig,
  ConseilConfigPaginatedCollectionSegment,
  FicheConseil,
  Produit,
  ProduitConseil,
} from '@lib/models/generated/graphql';
import { AuthService } from '@lib/services/auth-service.service';

import { AbstractStepComponent } from 'src/app/components/declare-fiche-conseil/steps/abstract-step.component';
import { FicheConseilService } from 'src/app/services/fiche-conseil.service';
import { OuiTooltipPosition } from 'omnium-ui/tooltip';

import { DEFAULT_PAGINATION_PARAMS, PaginationParams } from '@lib/components/paginated-table/paginated-table.component';
import { gql } from 'apollo-angular';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { deepCopy } from '@lib/utils/deepCopy';

import { ApplicationInsightsService, QueryManagerService } from 'finzzle-angular-utils';
import { FormConfigService } from 'src/app/services/form-config.service';
import { OuiSnackbarService } from 'omnium-ui/snackbar';
import { environment } from 'src/environments/environment';

const PAGECONTRATS = gql`
  query commandesPaginated($skip: Int, $take: Int, $where: CommandeFilterInput) {
    allCommandesFinancieresPaginated: commandesPaginated(skip: $skip, take: $take, where: $where) {
      pageInfo {
        hasNextPage
        hasPreviousPage
      }
      items {
        id
        nom
        produitId
        numeroContrat
        prixVenteTotalTTC
        dateActe
        produit {
          id
          nom
          nomTechniqueProduitBox
          idPartenaireNavigation {
            nom
          }
          enveloppe
          enveloppeNavigation {
            libelle
          }
          habilitation
        }
        investisseurCommande {
          investisseurId
          investisseur {
            id
            code
            statutCodeNavigation {
              libelle
              code
            }
            investisseurEntite {
              displayName
            }
          }
        }
      }
      totalCount
    }
  }
`;

const PRODUITS_FROM_CONFIGID = gql`
  query conseilConfigById($id: String!) {
    conseilConfigPaginated(take: 1, skip: 0, where: { id: { eq: $id } }) {
      items {
        configFormulaire {
          id

          produitConfigs {
            produit {
              id
            }
          }
        }
      }
    }
  }
`;

const PRODUIT_BY_ID = gql`
  query produitById($id: Long!) {
    produitById(id: $id) {
      id
      nom
      nomTechniqueProduitBox
      habilitation
      idPartenaireNavigation {
        id
        nom
      }
    }
  }
`;

@Component({
  selector: 'app-contrat-step',
  templateUrl: './contrat-step.component.html',
  styleUrls: ['./contrat-step.component.scss'],
})
export class ContratStepComponent extends AbstractStepComponent {
  @Input() contratId: number | null | undefined = null;
  @Input() isModifierDisabled: boolean = true;
  @Output() onContratSelection = new EventEmitter<Commande | null>();

  // request-filters
  enveloppeFilter: CommandeFilterInput = {};
  produitFilter: CommandeFilterInput = {};
  investisseurFilter: CommandeFilterInput = {};

  filterProduitId: number | null | undefined;
  filterEnveloppeId: number | null | undefined;
  filterInvestisseurId: number | null | undefined;
  filterContratId: number | null | undefined;

  whereFilter: CommandeFilterInput | undefined;

  // selected Contrat
  selectedContrat: Commande | null = null;
  selectedInvestisseurFullName: string;
  selectedCoInvestisseurFullName: string;
  isHabilitationValid: boolean = false;
  configProduitExists = true;

  selectedContratId: number | null | undefined = null;

  ContacterSupportAction = {
    label: 'Contacter le support',
    action: () => {
      window.open('https://prodemial.zendesk.com/hc/fr/requests/new?ticket_form_id=12954969451292&ticket=', '_blank');
    },
  };
  selectableProduits: any;
  conseilConfig: ConseilConfig | null = null;
  private missingAtlasProduitIds = new Set<number>();

  public override beforeFicheConseilUpdate(
    newFicheConseil: FicheConseil | null,
    previousFicheConseil: FicheConseil | null
  ): void {
  }
  public override isStepValid(): boolean {
    // If no contrat is selected, the step is not valid
    if (!this.selectedContrat) {
      return false;
    }
    // if we don't have a configProduitExists, we cannot proceed
    if (!this.configProduitExists) {
      return false;
    }
    return true;
  }
  public override markAsTouched(): void {
  }

  constructor(
    private queryManager: QueryManagerService,
    ficheConseilService: FicheConseilService,
    private userService: AuthService,
    private snackbarService: OuiSnackbarService,
    private loggerService: ApplicationInsightsService
  ) {
    super(ficheConseilService);
  }

  // contrat-table
  paginationParams: PaginationParams = DEFAULT_PAGINATION_PARAMS;
  requestResult: { loading: boolean; data: CommandesFinancieresPaginatedCollectionSegment } | undefined;

  //toolTip
  toolTipText: string = '';
  toolTipPosition: OuiTooltipPosition = 'bottom';

  override ngOnInit() {
    this.tryFetchCurrentContrat();
    this.paginationParams = DEFAULT_PAGINATION_PARAMS;
    this.ficheConseilService.currentFicheConseilSubject.subscribe(ficheConseil => {
      this.currentFicheConseil = ficheConseil;
      if (this.currentFicheConseil) {
        this.filterEnveloppeId = this.currentFicheConseil.enveloppe?.code ?? null;
        this.selectedContratId = this.currentFicheConseil.contratId ?? null;
        this.selectedInvestisseurFullName = this.currentFicheConseil.investisseur?.displayName ?? '';
        this.selectedCoInvestisseurFullName = this.currentFicheConseil.coInvestisseur?.displayName ?? '';
      }
    });

    if (this.isModifierDisabled) {
      this.toolTipText = 'Pour modifier le contrat, vous devez créer un nouvel acte de gestion.';
    }
  }

  tryFetchCurrentContrat() {
    if (this.contratId) {
      const numeroFilter: CommandeFilterInput = { id: { eq: this.contratId } };
      this.queryManager
        .query<{ allCommandesFinancieresPaginated: CommandesFinancieresPaginatedCollectionSegment }>({
          query: PAGECONTRATS,
          variables: {
            where: numeroFilter,
            skip: 0,
            take: 1,
          },
        })
        .subscribe(({ loading, data }) => {
          this.selectedContrat =
            (data.allCommandesFinancieresPaginated.items && data.allCommandesFinancieresPaginated.items[0]) ?? null;
          this.selectedContratId = this.selectedContrat?.id ?? null;

        });
    }
  }

  async getAllCommandes(): Promise<void> {
    this.whereFilter = this.buildCommandeFilter();
    let requestVariables: any;
    if (this.whereFilter) {
      requestVariables = { ...this.paginationParams, where: this.whereFilter };
    } else {
      requestVariables = { ...this.paginationParams };
    }

    if (this.requestResult) {
      // clear table and set loading when fetching data
      this.requestResult = {
        loading: true,
        data: { pageInfo: { hasNextPage: false, hasPreviousPage: false }, items: [], totalCount: 0 },
      };
    }
    const result = await firstValueFrom(
      this.queryManager.query<{ allCommandesFinancieresPaginated: CommandesFinancieresPaginatedCollectionSegment }>({
        query: PAGECONTRATS,
        variables: requestVariables,
      })
    );
    if (result) {
      //FIXME: Because ATLAS data is not always as reliable as it should be:  we need to filter the result.
      //Here we check if the user has access to all of the investisseurs of the Commande to avoid further complications (Atlas API will set investisseur to  null in the result if user cannot have access)
      const resultData = deepCopy(result.data.allCommandesFinancieresPaginated);

      resultData.items = resultData.items?.filter((contrat: Commande) =>
        contrat.investisseurCommande.every(ic => ic.investisseurId && ic.investisseur)
      );
      this.requestResult = { loading: result.loading, data: resultData };
    }
  }

  buildCommandeFilter(): CommandeFilterInput | undefined {
    const filter: CommandeFilterInput = {};
    // from AtlasDB: 0 = "Non conforme", 1 = "En-cours" , 2 = "Close"
    // Here we filter on etatCommande to get only active and non-conforme commandes
    // We also ensure there is at least one investisseur
    filter.and = [{ etatCommande: { in: [0, 1] } }, { investisseurCommande: { any: true } }];

    filter.and?.push({ produit: { etat: { in: [2, 3] } } });

    if (this.filterEnveloppeId) {
      const enveloppeFilter: CommandeFilterInput = {
        produit: { enveloppeNavigation: { code: { eq: this.filterEnveloppeId } } },
      };
      filter.and?.push(enveloppeFilter);
    } else {
      // by default exlude commande with product with no enveloppe. enveloppe are require to select form fields
      const enveloppeFilter: CommandeFilterInput = { produit: { enveloppe: { neq: null } } };
      filter.and?.push(enveloppeFilter);
    }

    if (this.filterContratId) {
      const contratFilter: CommandeFilterInput = { id: { eq: this.filterContratId } };
      filter.and?.push(contratFilter);
    }

    if (this.filterProduitId) {
      const produitFilter: CommandeFilterInput = { produitId: { eq: this.filterProduitId } };
      filter.and?.push(produitFilter);
    }
    if (this.filterInvestisseurId) {
      const investisseurFilter: CommandeFilterInput = {
        investisseurCommande: { some: { investisseurId: { eq: this.filterInvestisseurId } } },
      };
      filter.and?.push(investisseurFilter);
    }


    if (filter.and?.length === 0) {
      return undefined;
    }
    return filter;
  }

  onModifier() {
    this.selectedContrat = null;
    this.selectedInvestisseurFullName = '';
    this.onContratSelection.emit(this.selectedContrat);
  }

  hasProduitInConfig = (produitId: number): boolean => {
    const pcs = this.conseilConfig?.configFormulaire?.produitConfigs ?? [];
    return produitId !== null && pcs.some(pc => pc.produit?.id === produitId);
  };

  async onContratClick(contrat: Commande) {
    if (contrat) {
      this.selectedContrat = contrat;

      await this.getConseilConfigAndSelectableProduits();

      this.configProduitExists = this.hasProduitInConfig(contrat.produitId);

      this.onContratSelection.emit(this.selectedContrat);
      if (contrat.investisseurCommande) {
        this.selectedInvestisseurFullName = `${
          contrat.investisseurCommande[0]?.investisseur?.investisseurEntite?.displayName ?? 'non renseigné'
        }`;
        if (contrat.investisseurCommande[1]?.investisseur?.investisseurEntite)
          this.selectedCoInvestisseurFullName = `${
            contrat.investisseurCommande[1]?.investisseur?.investisseurEntite?.displayName ?? 'non renseigné'
          } `;
      }
    }
  }


  onInvestisseurIdSelected(investisseurId: number | null | undefined) {
    if (investisseurId !== this.filterInvestisseurId) {
      this.filterInvestisseurId = investisseurId;
      this.getAllCommandes();
    }
  }
  onCommandeIdSelected(commande: Commande | null | undefined) {
    if (this.selectedContratId != commande?.id) {
      this.selectedContratId = commande?.id;
      this.filterContratId = commande?.id;
      this.getAllCommandes();
    }
  }

  onEnveloppeIdSelected(enveloppeId: number | null | undefined) {
    if (enveloppeId !== this.filterEnveloppeId) {
      this.filterEnveloppeId = enveloppeId;
      this.filterProduitId = undefined;
      this.getAllCommandes();
    }
  }

  onProduitSelected(produit: Produit | null | undefined) {
    if (produit?.id !== this.filterProduitId) {
      this.filterProduitId = produit?.id;
      this.getAllCommandes();
    }
  }

  async getConseilConfigAndSelectableProduits(): Promise<void> {
    const resultFromRequest = await lastValueFrom(
      this.queryManager.query<{ conseilConfigPaginated: ConseilConfigPaginatedCollectionSegment }>({
        query: PRODUITS_FROM_CONFIGID,
        variables: {
          id: this.currentFicheConseil?.conseilConfigId,
        },
      })
    );
    if (!resultFromRequest.data?.conseilConfigPaginated?.items?.length) {
      this.selectableProduits;
      return;
    }
    this.conseilConfig = resultFromRequest.data?.conseilConfigPaginated?.items[0];

    // filter selectableProduits
    const configProduits =
      this.conseilConfig?.configFormulaire?.produitConfigs?.map(configProduit => configProduit.produit) ?? [];
    const enrichedProduits = await this.enrichProduitsFromAtlas(configProduits);
    this.selectableProduits = enrichedProduits
      .filter(p => !!p && (!p.habilitation || this.userService.getUserValidHabilitationIds().includes(p.habilitation)))
      .map(produit => {
        return {
          id: produit!.id,
          libelle: produit!.nomTechniqueProduitBox ?? produit!.nom ?? 'Produit sans nom',
          partenaireId: produit!.idPartenaireNavigation?.id,
          partenaireNom: produit!.idPartenaireNavigation?.nom,
        } as ProduitConseil;
      });
  }

  private async enrichProduitsFromAtlas(produits: (Produit | null | undefined)[]): Promise<Produit[]> {
    const enrichedProduits = await Promise.all(
      produits
        .filter((produit): produit is Produit => !!produit)
        .map(async produit => {
          const atlasProduit = await this.getAtlasProduitById(produit.id);
          return {
            ...produit,
            ...atlasProduit,
            id: produit.id,
            nom: atlasProduit?.nom ?? this.getMissingAtlasProduitLabel(produit.id),
          } as Produit;
        })
    );

    return enrichedProduits;
  }

  private async getAtlasProduitById(id: number): Promise<Produit | null> {
    try {
      const result = await firstValueFrom(
        this.queryManager.query<{ produitById: Produit | null }>(
          {
            query: PRODUIT_BY_ID,
            variables: { id },
            fetchPolicy: 'network-only',
            errorPolicy: 'all',
          },
          true
        )
      );

      const produit = result.data?.produitById ?? null;
      if (!produit) {
        this.notifyMissingAtlasProduit(id);
      }

      return produit;
    } catch {
      this.notifyMissingAtlasProduit(id);
      return null;
    }
  }

  private getMissingAtlasProduitLabel(id: number): string {
    return environment.production
      ? `Produit ${id} introuvable dans Atlas - contactez le support`
      : `Produit ${id} absent d'Atlas (possible hors prod)`;
  }

  private notifyMissingAtlasProduit(produitId: number): void {
    if (this.missingAtlasProduitIds.has(produitId)) return;

    this.missingAtlasProduitIds.add(produitId);
    const missingProduitIds = Array.from(this.missingAtlasProduitIds).join(', ');

    if (environment.production) {
      this.loggerService.logEvent('MissingAtlasProduitInContratStep', {
        produitId,
        configId: this.currentFicheConseil?.conseilConfigId,
      });
      this.snackbarService.open(
        `Produits introuvables dans Atlas : ${missingProduitIds}. Merci de contacter le support.`,
        'error',
        7000
      );
    } else {
      this.snackbarService.open(
        `Produits absents d'Atlas : ${missingProduitIds}. En hors prod, cela peut arriver.`,
        'warning',
        7000
      );
    }
  }
}
